import { useState } from 'react';
import { isoVersDate } from '../../api/mappers';
import { devisApi, facturesApi } from '../../api/resources';
import { peutEncaisser } from '../../lib/access';
import { aujourdhuiIso } from '../../lib/utils';
import { ttcDocument } from '../../lib/vat';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const etape = { display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 10, marginTop: 5 };
const action = (couleur) => ({
  border: 'none',
  borderRadius: 9,
  background: couleur,
  color: '#fff',
  fontSize: 10.5,
  fontWeight: 800,
  padding: '9px 10px',
  width: '100%',
  marginTop: 7,
  cursor: 'pointer',
});

/** En dessous de ce montant, l'avancement ne vaut pas une facture. */
const AVANCEMENT_MINIMUM = 100;

const petit = (couleur) => ({
  border: '1px solid ' + couleur,
  background: '#fff',
  color: couleur,
  borderRadius: 7,
  fontSize: 9.5,
  fontWeight: 800,
  padding: '4px 8px',
  cursor: 'pointer',
});

/**
 * Une facture de l'affaire, avec où elle en est — et, sans relevé bancaire,
 * de quoi dire à la main qu'elle est partie ou qu'elle est payée.
 */
function Piece({ f, libelle, onNav, onEnvoyee, onPayee, onNonPayee }) {
  const [saisie, setSaisie] = useState(null);
  // Réglée par un avoir, ou l'avoir lui-même : rien à encaisser.
  const payee = f.statut === 'Payée' || !!f.regleeParAvoir || !!f.avoir;
  const partielle = f.statut === 'Partielle';
  const brouillon = f.statut === 'Brouillon';
  const etat = f.avoir
    ? tr('avoir émis')
    : f.regleeParAvoir
      ? tr('annulée par un avoir')
      : payee
        ? f.payeeLe
          ? trf('payée le {0}', f.payeeLe)
          : tr('payée')
        : partielle && !(f.retard > 0)
          ? trf('payée en partie : {0} reçus', DEVISE + ' ' + fmt(f.percu || 0))
          : brouillon
            ? tr('prête, pas encore envoyée')
            : f.retard > 0
              ? trf('en retard de {0} jour(s)', f.retard)
              : tr('envoyée, pas encore payée');
  const couleur = payee ? C.green : brouillon ? C.purple : f.retard > 0 ? C.red : C.amber;
  return (
    <div style={{ marginTop: 5 }}>
      <div
        role={brouillon || f.retard > 0 ? 'button' : undefined}
        onClick={brouillon || f.retard > 0 ? () => onNav('factures') : undefined}
        style={{
          ...etape,
          marginTop: 0,
          cursor: brouillon || f.retard > 0 ? 'pointer' : 'default',
        }}
      >
        <span>{payee ? '✅' : brouillon ? '✏️' : f.retard > 0 ? '⏰' : '⏳'}</span>
        <span style={{ flex: 1, color: C.ink }}>
          {trf('{0} n° {1}', tr(libelle), f.num)}
          <span style={{ color: couleur, fontWeight: 700 }}>
            {' · '}
            {etat}
          </span>
        </span>
        <b style={{ color: C.ink }}>
          {DEVISE} {fmt(f.ttc)}
        </b>
      </div>
      {brouillon && onEnvoyee && (
        <div style={{ display: 'flex', gap: 6, margin: '4px 0 0 21px' }}>
          <button type="button" style={petit(C.purple)} onClick={() => onEnvoyee(f.id)}>
            {tr('📨 Je l’ai envoyée')}
          </button>
        </div>
      )}
      {payee && f.statut === 'Payée' && onNonPayee && (
        <div style={{ display: 'flex', gap: 6, margin: '4px 0 0 21px' }}>
          <button
            type="button"
            style={petit(C.amber)}
            onClick={() => {
              if (
                window.confirm(
                  trf(
                    'Remettre la facture {0} en « non payée » ? Le paiement noté sera retiré.',
                    f.num,
                  ),
                )
              )
                onNonPayee(f.id);
            }}
          >
            {tr('↩️ Remettre non payée')}
          </button>
        </div>
      )}
      {!payee && !brouillon && onPayee && saisie === null && (
        <div style={{ display: 'flex', gap: 6, margin: '4px 0 0 21px' }}>
          <button type="button" style={petit(C.green)} onClick={() => setSaisie(aujourdhuiIso())}>
            {tr('✓ Le client a payé')}
          </button>
        </div>
      )}
      {saisie !== null && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            flexWrap: 'wrap',
            margin: '5px 0 0 21px',
          }}
        >
          <span style={{ fontSize: 9.5, color: C.grey }}>{tr('Payée le')}</span>
          <input
            type="date"
            aria-label={tr('Date du paiement')}
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            style={{
              border: '1px solid ' + C.border,
              borderRadius: 7,
              padding: '4px 6px',
              fontSize: 10,
            }}
          />
          <button
            type="button"
            style={{ ...petit(C.green), background: C.green, color: '#fff' }}
            onClick={() => {
              onPayee(f.id, isoVersDate(saisie || aujourdhuiIso()));
              setSaisie(null);
            }}
          >
            {tr('Enregistrer')}
          </button>
          <button type="button" style={petit(C.grey)} onClick={() => setSaisie(null)}>
            {tr('Annuler')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Une affaire, chantier par chantier : l'offre acceptée, les travaux en plus,
 * les acomptes, la facture finale — et ce qu'il reste à demander au client.
 *
 * <p>Avec deux chantiers en cours, chacun avec son acompte, le total seul ne
 * disait pas lequel attendait sa facture finale. Quand les travaux sont finis,
 * la facture finale se prépare ici d'un toucher (offre, avenants, acomptes
 * déduits) ; en cours de route, l'avancement se facture de la même façon.
 * La facture sort en brouillon : le patron la relit avant de l'envoyer.
 *
 * @param {object} props propriétés
 * @param {object} props.a affaire rendue par `suiviAffaires`
 * @param {string} props.titre nom du chantier ou du client
 * @param {(module: string) => void} props.onNav navigation
 * @param {() => Promise<void>|void} [props.rechargeFactures] relit les factures après une création
 * @param {Function} [props.setFactures] met à jour les factures : envoyée ou payée, à la main
 * @param {boolean} [props.replie] une seule ligne, le détail s'ouvre d'un toucher (accueil)
 * @returns {import('react').ReactElement} le bloc de l'affaire
 */
export function AffaireChantier({
  a,
  titre,
  onNav,
  rechargeFactures,
  setFactures,
  replie = false,
}) {
  const [ouvert, setOuvert] = useState(!replie);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState(null);
  const pct = (v) => (a.total > 0 ? Math.max(0, Math.min(100, (v / a.total) * 100)) : 0);
  const finalePrete = a.enPreparation.some((f) => !f.acompte);
  const finaleAFaire = a.finales.every((f) => f.regleeParAvoir) && !finalePrete && a.reste > 0.005;
  const peutAvancer =
    !a.termine &&
    a.finales.every((f) => f.regleeParAvoir) &&
    a.avancement > 0 &&
    a.avancementAFacturer >= AVANCEMENT_MINIMUM;

  // Sans relevé bancaire, le patron dit lui-même qu'une facture est partie
  // ou qu'elle est payée : l'accueil, la comptabilité et la TVA suivent.
  const encaisse = !!setFactures && peutEncaisser();
  const onPayee = encaisse
    ? (id, date) =>
        setFactures((p) =>
          p.map((x) =>
            x.id === id ? { ...x, statut: 'Payée', paidDate: date, paidAmount: ttcDocument(x) } : x,
          ),
        )
    : null;
  // Un « payée » noté par erreur se défait.
  const onNonPayee = encaisse
    ? (id) =>
        setFactures((p) =>
          p.map((x) =>
            x.id === id ? { ...x, statut: 'Envoyée', paidDate: '', paidAmount: 0 } : x,
          ),
        )
    : null;
  const onEnvoyee = setFactures
    ? (id) =>
        setFactures((p) =>
          p.map((x) =>
            x.id === id
              ? { ...x, statut: 'Envoyée', envoyeLe: x.envoyeLe || isoVersDate(aujourdhuiIso()) }
              : x,
          ),
        )
    : null;
  const actions = { onNav, onPayee, onEnvoyee, onNonPayee };

  // Ce qu'il y a à faire, en un mot : la ligne repliée de l'accueil le dit.
  const enRetard = [...a.acomptes, ...a.finales].some((f) => f.retard > 0);
  const [aFaire, couleurAFaire] =
    finaleAFaire && a.termine
      ? [tr('⚠️ Facture finale à faire'), C.amber]
      : enRetard
        ? [tr('⏰ Paiement en retard'), C.red]
        : a.enPreparation.length
          ? [tr('✏️ Facture à envoyer'), C.purple]
          : peutAvancer
            ? [tr('📈 Avancement à facturer'), C.teal]
            : a.facture - a.paye > 0.005
              ? [tr('⏳ En attente de paiement'), C.amber]
              : a.reste <= 0.005
                ? [tr('✓ Tout est facturé'), C.green]
                : [tr('🔨 Travaux en cours'), C.grey];

  const prepare = async (creer, texteOk) => {
    if (enCours) return;
    setEnCours(true);
    setMessage(null);
    try {
      const f = await creer();
      if (rechargeFactures) await rechargeFactures();
      setMessage({ ok: true, texte: trf(texteOk, f?.numero || '') });
    } catch (e) {
      setMessage({ ok: false, texte: e?.message || tr('La facture n’a pas pu être préparée.') });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div
      data-testid="affaire"
      style={{
        border: '1px solid ' + C.border,
        borderRadius: 12,
        padding: '10px 11px',
        marginBottom: 8,
        background: C.white,
      }}
    >
      <div
        role={replie ? 'button' : undefined}
        aria-expanded={replie ? ouvert : undefined}
        onClick={replie ? () => setOuvert((o) => !o) : undefined}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          cursor: replie ? 'pointer' : 'default',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11.5, fontWeight: 900, color: C.ink }}>🏗️ {titre}</div>
          {replie && (
            <div style={{ fontSize: 9.5, fontWeight: 800, color: couleurAFaire, marginTop: 2 }}>
              {aFaire}
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 900,
            color: a.reste > 0.005 ? C.navy : C.green,
            whiteSpace: 'nowrap',
          }}
        >
          {a.reste > 0.005 ? trf('reste {0}', DEVISE + ' ' + fmt(a.reste)) : tr('✓ tout facturé')}
          {replie && <span style={{ color: C.muted, marginLeft: 6 }}>{ouvert ? '▾' : '▸'}</span>}
        </div>
      </div>
      {ouvert && (
        <>
          <div
            style={{
              display: 'flex',
              height: 7,
              borderRadius: 7,
              overflow: 'hidden',
              background: C.border,
              margin: '7px 0 4px',
            }}
          >
            <div style={{ width: pct(a.paye) + '%', background: C.green }} />
            <div style={{ width: pct(a.facture - a.paye) + '%', background: C.amber }} />
          </div>
          <div
            style={{ display: 'flex', gap: 10, fontSize: 8.5, color: C.muted, flexWrap: 'wrap' }}
          >
            <span>
              <b style={{ color: C.green }}>■</b> {tr('payé')} {fmt(a.paye)}
            </span>
            <span>
              <b style={{ color: C.amber }}>■</b> {tr('facturé, pas payé')}{' '}
              {fmt(a.facture - a.paye)}
            </span>
            <span>
              <b style={{ color: C.muted }}>■</b> {tr('à facturer')} {fmt(a.reste)}
            </span>
          </div>

          <div style={etape}>
            <span>📄</span>
            <span style={{ flex: 1, color: C.ink }}>{tr('Offre acceptée')}</span>
            <b style={{ color: C.ink }}>
              {DEVISE} {fmt(a.offreTtc)}
            </b>
          </div>
          {a.avenantsTtc > 0 && (
            <div style={etape}>
              <span>➕</span>
              <span style={{ flex: 1, color: C.ink }}>{tr('Travaux en plus (avenants)')}</span>
              <b style={{ color: C.ink }}>
                {DEVISE} {fmt(a.avenantsTtc)}
              </b>
            </div>
          )}
          {/* Le premier acompte, puis les factures d'avancement. */}
          {[...a.acomptes, ...a.enPreparation.filter((f) => f.acompte)].map((f, i) => (
            <Piece
              key={f.id}
              f={f}
              libelle={i === 0 ? 'Acompte' : 'Facture d’avancement'}
              {...actions}
            />
          ))}
          {a.finales.map((f) => (
            <Piece key={f.id} f={f} libelle="Facture finale" {...actions} />
          ))}
          {(a.avoirs || []).map((f) => (
            <Piece key={f.id} f={f} libelle="Avoir" {...actions} />
          ))}
          {a.enPreparation
            .filter((f) => !f.acompte)
            .map((f) => (
              <Piece key={f.id} f={f} libelle="Facture finale" {...actions} />
            ))}

          {peutAvancer && (
            <div
              style={{
                background: C.tealL,
                borderRadius: 9,
                padding: '8px 9px',
                marginTop: 7,
              }}
            >
              <div style={{ ...etape, marginTop: 0 }}>
                <span>📈</span>
                <span style={{ flex: 1, color: C.teal, fontWeight: 800 }}>
                  {trf('Travaux à {0} % : tu peux déjà facturer', a.avancement)}
                </span>
                <b style={{ color: C.teal }}>
                  {DEVISE} {fmt(a.avancementAFacturer)}
                </b>
              </div>
              <button
                type="button"
                disabled={enCours}
                style={action(C.teal)}
                onClick={() =>
                  prepare(
                    () => devisApi.factureAvancement(a.id, a.avancement),
                    '✓ Facture d’avancement n° {0} prête : relis-la puis envoie-la.',
                  )
                }
              >
                {enCours ? tr('Préparation…') : tr('📈 Préparer la facture d’avancement')}
              </button>
            </div>
          )}

          {finaleAFaire &&
            (a.termine ? (
              <div
                style={{
                  background: C.amber + '18',
                  border: '1px solid ' + C.amber + '55',
                  borderRadius: 9,
                  padding: '8px 9px',
                  marginTop: 7,
                }}
              >
                <div style={{ ...etape, marginTop: 0 }}>
                  <span>⚠️</span>
                  <span style={{ flex: 1, color: C.amber, fontWeight: 800 }}>
                    {tr('Travaux finis : facture finale à faire')}
                  </span>
                  <b style={{ color: C.amber }}>
                    {DEVISE} {fmt(a.reste)}
                  </b>
                </div>
                <button
                  type="button"
                  disabled={enCours}
                  style={action(C.amber)}
                  onClick={() =>
                    prepare(
                      () => facturesApi.depuisDevis(a.id),
                      '✓ Facture finale n° {0} prête (acomptes déduits) : relis-la puis envoie-la.',
                    )
                  }
                >
                  {enCours ? tr('Préparation…') : tr('🧾 Préparer la facture finale')}
                </button>
              </div>
            ) : (
              <div style={etape}>
                <span>🔨</span>
                <span style={{ flex: 1, color: C.grey }}>
                  {a.chantierId != null
                    ? trf('Travaux en cours ({0} %) · facture finale à la fin', a.avancement)
                    : tr('Facture finale à faire')}
                </span>
                <b style={{ color: C.grey }}>
                  {DEVISE} {fmt(a.reste)}
                </b>
              </div>
            ))}

          {message && (
            <div
              role={message.ok ? 'status' : 'alert'}
              onClick={message.ok ? () => onNav('factures') : undefined}
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                marginTop: 7,
                color: message.ok ? C.green : C.red,
                cursor: message.ok ? 'pointer' : 'default',
              }}
            >
              {message.texte}
              {message.ok ? ' ›' : ''}
            </div>
          )}
        </>
      )}
    </div>
  );
}
